import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  BarChart3,
  Boxes,
  ChevronRight,
  ImageIcon,
  Loader2,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react'
import { api } from '@/lib/api'
import { cn, formatDateTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/toaster'
import type {
  MaterialAnalyticsChainItem,
  MaterialAnalyticsDailyStats,
  MaterialAnalyticsImageSearchResult,
  MaterialAnalyticsProduct,
} from '@/types'

const PAGE_SIZE = 30

function defaultStartDate(): string {
  const date = new Date()
  date.setDate(date.getDate() - 7)
  return date.toISOString().slice(0, 10)
}

function toStartIso(value: string): string | undefined {
  if (!value) return undefined
  return new Date(`${value}T00:00:00`).toISOString()
}

function toEndIso(value: string): string | undefined {
  if (!value) return undefined
  return new Date(`${value}T23:59:59`).toISOString()
}

export function MaterialAnalyticsPage() {
  const queryClient = useQueryClient()
  const [startDate, setStartDate] = useState(defaultStartDate())
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10))
  const [authorEmail, setAuthorEmail] = useState('')
  const [productId, setProductId] = useState('')
  const [taskType, setTaskType] = useState('')
  const [downloaded, setDownloaded] = useState('')
  const [toolId, setToolId] = useState('')
  const [offset, setOffset] = useState(0)
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [searchFiles, setSearchFiles] = useState<File[]>([])
  const [productDraft, setProductDraft] = useState({
    product_name: '',
    description: '',
    target_market: '',
    target_audience: '',
    selling_points: '',
  })

  const queryParams = useMemo(
    () => ({
      start_date: toStartIso(startDate),
      end_date: toEndIso(endDate),
      author_email: authorEmail.trim() || undefined,
      product_id: productId || undefined,
      task_type: taskType || undefined,
      tool_id: toolId || undefined,
      is_downloaded: downloaded === '' ? undefined : downloaded === 'true',
    }),
    [authorEmail, downloaded, endDate, productId, startDate, taskType, toolId],
  )

  // 统计条/趋势图是下载率的分母，刻意不随下载筛选收窄（否则占比恒为 100%/0%）
  const summaryParams = useMemo(() => {
    const { is_downloaded: _omit, ...rest } = queryParams
    return rest
  }, [queryParams])

  const productsQuery = useQuery({
    queryKey: ['material-analytics-products'],
    queryFn: () => api.getMaterialAnalyticsProducts(),
  })

  const summaryQuery = useQuery({
    queryKey: ['material-analytics-summary', summaryParams],
    queryFn: () => api.getMaterialAnalyticsSummary(summaryParams),
  })

  const authorEmails = useMemo(
    () => (summaryQuery.data?.author_stats ?? []).map((a) => a.author_email),
    [summaryQuery.data],
  )

  const assetsQuery = useQuery({
    queryKey: ['material-analytics-assets', queryParams, offset],
    queryFn: () =>
      api.getMaterialAnalyticsAssets({
        ...queryParams,
        offset,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  })

  const detailQuery = useQuery({
    queryKey: ['material-analytics-chain', selectedAssetId],
    queryFn: () => api.getMaterialAnalyticsChain(selectedAssetId as string),
    enabled: Boolean(selectedAssetId),
  })

  const searchMutation = useMutation({
    mutationFn: (files: File[]) => api.searchMaterialAnalyticsImages(files),
    onError: () => {
      toast({ title: '图片搜索失败', variant: 'destructive' })
    },
  })

  const createProductMutation = useMutation({
    mutationFn: () =>
      api.createMaterialAnalyticsProduct({
        product_name: productDraft.product_name,
        description: productDraft.description || undefined,
        target_market: productDraft.target_market || undefined,
        target_audience: productDraft.target_audience || undefined,
        selling_points: productDraft.selling_points || undefined,
        language: 'en',
        metadata: { material_analytics: true },
      }),
    onSuccess: () => {
      setProductDraft({
        product_name: '',
        description: '',
        target_market: '',
        target_audience: '',
        selling_points: '',
      })
      queryClient.invalidateQueries({ queryKey: ['material-analytics-products'] })
      toast({ title: '分析产品已创建' })
    },
    onError: () => {
      toast({ title: '创建分析产品失败', variant: 'destructive' })
    },
  })

  const bindProductMutation = useMutation({
    mutationFn: (params: { assetId: string; productId: string | null }) =>
      api.bindMaterialAnalyticsAssetProduct(params.assetId, params.productId),
    onSuccess: (chain) => {
      setSelectedAssetId(chain.output.id)
      queryClient.invalidateQueries({ queryKey: ['material-analytics-assets'] })
      queryClient.invalidateQueries({ queryKey: ['material-analytics-chain', chain.output.id] })
      toast({ title: '素材产品绑定已更新' })
    },
    onError: () => {
      toast({ title: '绑定产品失败', variant: 'destructive' })
    },
  })

  const selectedFromList = assetsQuery.data?.items.find((item) => item.output.id === selectedAssetId) ?? null
  const selectedChain = detailQuery.data ?? selectedFromList

  const total = assetsQuery.data?.total ?? 0
  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + PAGE_SIZE, total)

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            一期覆盖图片素材，按作者、日期、产品和生成任务查看现有可还原链路。
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            summaryQuery.refetch()
            assetsQuery.refetch()
          }}
          disabled={summaryQuery.isFetching || assetsQuery.isFetching}
        >
          <RefreshCw className={cn('h-4 w-4 mr-2', (summaryQuery.isFetching || assetsQuery.isFetching) && 'animate-spin')} />
          刷新
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7">
          <FilterField label="开始日期">
            <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </FilterField>
          <FilterField label="结束日期">
            <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </FilterField>
          <FilterField label="作者邮箱">
            <Input
              value={authorEmail}
              onChange={(event) => setAuthorEmail(event.target.value)}
              placeholder="email"
            />
          </FilterField>
          <FilterField label="分析产品">
            <select
              value={productId}
              onChange={(event) => {
                setProductId(event.target.value)
                setOffset(0)
              }}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">全部</option>
              {(productsQuery.data ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="任务类型">
            <select
              value={taskType}
              onChange={(event) => {
                setTaskType(event.target.value)
                setOffset(0)
              }}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">全部</option>
              <option value="image_freestyle">image_freestyle</option>
              <option value="competitor_research">competitor_research</option>
              <option value="variation">variation</option>
            </select>
          </FilterField>
          <FilterField label="下载">
            <select
              value={downloaded}
              onChange={(event) => {
                setDownloaded(event.target.value)
                setOffset(0)
              }}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">全部</option>
              <option value="true">已下载</option>
              <option value="false">未下载</option>
            </select>
          </FilterField>
          <FilterField label="模型">
            <Input
              value={toolId}
              onChange={(event) => {
                setToolId(event.target.value)
                setOffset(0)
              }}
              placeholder="gpt_image"
            />
          </FilterField>
        </div>
      </div>

      <SummaryStrip summary={summaryQuery.data} loading={summaryQuery.isLoading} />

      <Tabs defaultValue="assets" className="space-y-3">
        <TabsList>
          <TabsTrigger value="assets">
            <ImageIcon className="h-4 w-4 mr-1.5" />
            图片素材
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search className="h-4 w-4 mr-1.5" />
            图片搜索
          </TabsTrigger>
          <TabsTrigger value="products">
            <Boxes className="h-4 w-4 mr-1.5" />
            分析产品库
          </TabsTrigger>
          <TabsTrigger value="authors">
            <Users className="h-4 w-4 mr-1.5" />
            作者统计
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assets">
          <div className="grid gap-4 xl:grid-cols-11">
            <div className="rounded-lg border border-gray-200 bg-white xl:col-span-5">
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <div className="text-sm text-muted-foreground">
                  {assetsQuery.isLoading ? '加载中' : `输入图组 ${pageStart}-${pageEnd} / ${total}`}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    下一页
                  </Button>
                </div>
              </div>
              <AssetTable
                items={assetsQuery.data?.items ?? []}
                loading={assetsQuery.isLoading}
                selectedAssetId={selectedAssetId}
                onSelect={setSelectedAssetId}
              />
            </div>
            <div className="xl:col-span-6">
              <ChainDetail
                chain={selectedChain}
                products={productsQuery.data ?? []}
                loading={detailQuery.isFetching}
                onBind={(assetId, nextProductId) => bindProductMutation.mutate({ assetId, productId: nextProductId })}
                binding={bindProductMutation.isPending}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="search">
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => setSearchFiles(Array.from(event.target.files ?? []))}
                className="max-w-md"
              />
              <Button
                disabled={!searchFiles.length || searchMutation.isPending}
                onClick={() => searchMutation.mutate(searchFiles)}
              >
                {searchMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                搜索图片
              </Button>
              <span className="text-xs text-muted-foreground">按 MD5 精确匹配，pHash 返回相似候选。</span>
            </div>
            <SearchResults results={searchMutation.data ?? []} onSelect={setSelectedAssetId} />
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
            <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
              <h2 className="text-base font-semibold">新建分析产品</h2>
              <Input
                placeholder="产品名称"
                value={productDraft.product_name}
                onChange={(event) => setProductDraft({ ...productDraft, product_name: event.target.value })}
              />
              <Textarea
                placeholder="产品描述"
                value={productDraft.description}
                onChange={(event) => setProductDraft({ ...productDraft, description: event.target.value })}
              />
              <Input
                placeholder="目标市场"
                value={productDraft.target_market}
                onChange={(event) => setProductDraft({ ...productDraft, target_market: event.target.value })}
              />
              <Input
                placeholder="目标受众"
                value={productDraft.target_audience}
                onChange={(event) => setProductDraft({ ...productDraft, target_audience: event.target.value })}
              />
              <Textarea
                placeholder="卖点，每行一个"
                value={productDraft.selling_points}
                onChange={(event) => setProductDraft({ ...productDraft, selling_points: event.target.value })}
              />
              <Button
                disabled={!productDraft.product_name || createProductMutation.isPending}
                onClick={() => createProductMutation.mutate()}
              >
                {createProductMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                创建产品
              </Button>
            </div>
            <ProductList products={productsQuery.data ?? []} loading={productsQuery.isLoading} />
          </div>
        </TabsContent>

        <TabsContent value="authors">
          <div className="space-y-4">
          <DownloadTrendChart
            daily={summaryQuery.data?.daily_stats ?? []}
            authors={authorEmails}
            summaryParams={summaryParams}
          />
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-3 font-medium">作者邮箱统计</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">作者</th>
                    <th className="px-4 py-2 text-right">任务</th>
                    <th className="px-4 py-2 text-right">图片</th>
                    <th className="px-4 py-2 text-right">成功任务</th>
                    <th className="px-4 py-2 text-right">失败任务</th>
                    <th className="px-4 py-2 text-right">Credits</th>
                    <th className="px-4 py-2 text-right">已下载</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(summaryQuery.data?.author_stats ?? []).map((author) => (
                    <tr key={author.author_email}>
                      <td className="px-4 py-2 font-medium text-gray-900">{author.author_email}</td>
                      <td className="px-4 py-2 text-right">{author.task_count}</td>
                      <td className="px-4 py-2 text-right">{author.output_image_count}</td>
                      <td className="px-4 py-2 text-right">{author.completed_task_count}</td>
                      <td className="px-4 py-2 text-right">{author.failed_task_count}</td>
                      <td className="px-4 py-2 text-right">{author.credits_consumed}</td>
                      <td className="px-4 py-2 text-right">{author.downloaded_image_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

/** 按周聚合：返回该日期所在周的周一（作为周标签）。纯 UTC 运算，与浏览器时区无关。 */
function weekStart(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

// SVG 图表尺寸常量
const TREND_STEP = 64 // 每个周期的横向占位
const TREND_BAR_AREA = 144 // 柱体区高度（同时是下载率折线 0-100% 的值域）
const TREND_TOP = 18 // 顶部占比文字区
const TREND_BOTTOM = 30 // 底部两行标签区

function DownloadTrendChart({
  daily,
  authors,
  summaryParams,
}: {
  daily: MaterialAnalyticsDailyStats[]
  authors: string[]
  summaryParams: Parameters<typeof api.getMaterialAnalyticsSummary>[0]
}) {
  const [granularity, setGranularity] = useState<'day' | 'week'>('day')
  const [author, setAuthor] = useState('')

  // 页面筛选变化导致选中作者从选项中消失时，重置选择（否则下拉显示空白但数据仍是旧作者的）
  useEffect(() => {
    if (author && authors.length > 0 && !authors.includes(author)) {
      setAuthor('')
    }
  }, [author, authors])

  // 选中作者时单独拉一份该作者的 summary（精确匹配，避免邮箱互为子串时混入他人数据）
  const authorQuery = useQuery({
    queryKey: ['material-analytics-author-trend', summaryParams, author],
    queryFn: () => api.getMaterialAnalyticsSummary({ ...summaryParams, author_email_exact: author }),
    enabled: Boolean(author),
  })
  const sourceDaily = author ? authorQuery.data?.daily_stats ?? [] : daily

  const points = useMemo(() => {
    if (granularity === 'day') {
      return sourceDaily.map((row) => ({
        key: row.date, // 完整日期做 React key，避免跨年 MM-DD 冲突
        label: row.date.slice(5), // MM-DD
        generated: row.output_image_count,
        downloaded: row.downloaded_image_count,
      }))
    }
    const byWeek = new Map<string, { generated: number; downloaded: number }>()
    for (const row of sourceDaily) {
      const key = weekStart(row.date)
      const acc = byWeek.get(key) ?? { generated: 0, downloaded: 0 }
      acc.generated += row.output_image_count
      acc.downloaded += row.downloaded_image_count
      byWeek.set(key, acc)
    }
    return [...byWeek.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, acc]) => ({ key, label: `${key.slice(5)}周`, ...acc }))
  }, [sourceDaily, granularity])

  const maxGenerated = Math.max(1, ...points.map((p) => p.generated))
  // 有量才渲染柱体（最低 3px 保证可见），0 不画幽灵柱
  const barHeight = (value: number) =>
    value > 0 ? Math.max(3, (value / maxGenerated) * TREND_BAR_AREA) : 0
  const ratioY = (ratio: number) => TREND_TOP + (1 - ratio) * TREND_BAR_AREA

  const chartWidth = points.length * TREND_STEP
  const chartHeight = TREND_TOP + TREND_BAR_AREA + TREND_BOTTOM
  // 下载率折线：仅连接有生成量的周期
  const linePoints = points
    .map((p, i) =>
      p.generated > 0
        ? `${i * TREND_STEP + TREND_STEP / 2},${ratioY(p.downloaded / p.generated).toFixed(1)}`
        : null,
    )
    .filter(Boolean)
    .join(' ')

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
        <div className="font-medium">下载趋势（下载图片数 / 生成图片数）</div>
        <div className="ml-auto flex items-center gap-2">
          <select
            className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
            value={author}
            onChange={(event) => setAuthor(event.target.value)}
          >
            <option value="">全部作者</option>
            {authors.map((email) => (
              <option key={email} value={email}>
                {email.split('@')[0]}
              </option>
            ))}
          </select>
          <div className="flex rounded-md border border-gray-200 text-xs">
            {(['day', 'week'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                className={cn(
                  'px-3 py-1.5 first:rounded-l-md last:rounded-r-md',
                  granularity === g ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                {g === 'day' ? '按日' : '按周'}
              </button>
            ))}
          </div>
        </div>
      </div>
      {author && authorQuery.isLoading ? (
        <div className="flex justify-center px-4 py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : author && authorQuery.isError ? (
        <div className="px-4 py-10 text-center text-sm text-red-500">
          作者数据加载失败
          <button
            type="button"
            className="ml-2 text-blue-600 hover:underline"
            onClick={() => authorQuery.refetch()}
          >
            重试
          </button>
        </div>
      ) : points.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">当前筛选条件下没有数据。</div>
      ) : (
        <div className="px-4 py-4">
          <div className="overflow-x-auto pb-1">
            <svg width={chartWidth} height={chartHeight} className="block">
              {points.map((point, index) => {
                const x = index * TREND_STEP + TREND_STEP / 2
                const ratio = point.generated > 0 ? point.downloaded / point.generated : null
                const ratioLabel = ratio !== null ? `${(ratio * 100).toFixed(1)}%` : '-'
                const gH = barHeight(point.generated)
                const dH = barHeight(point.downloaded)
                return (
                  <g key={point.key}>
                    <title>
                      {`${point.key} · 生成 ${point.generated} · 下载 ${point.downloaded} · 下载率 ${ratioLabel}`}
                    </title>
                    <rect
                      x={x - 18}
                      y={TREND_TOP + TREND_BAR_AREA - gH}
                      width={16}
                      height={gH}
                      rx={2}
                      fill="#bfdbfe"
                    />
                    <rect
                      x={x + 2}
                      y={TREND_TOP + TREND_BAR_AREA - dH}
                      width={16}
                      height={dH}
                      rx={2}
                      fill="#10b981"
                    />
                    <text x={x} y={TREND_TOP - 6} textAnchor="middle" fontSize="10" fill="#047857">
                      {ratioLabel}
                    </text>
                    <text
                      x={x}
                      y={TREND_TOP + TREND_BAR_AREA + 12}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#6b7280"
                    >
                      {point.label}
                    </text>
                    <text
                      x={x}
                      y={TREND_TOP + TREND_BAR_AREA + 24}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#9ca3af"
                    >
                      {point.downloaded}/{point.generated}
                    </text>
                  </g>
                )
              })}
              {linePoints && (
                <polyline points={linePoints} fill="none" stroke="#f59e0b" strokeWidth="1.5" />
              )}
              {points.map((point, index) =>
                point.generated > 0 ? (
                  <circle
                    key={`dot-${point.key}`}
                    cx={index * TREND_STEP + TREND_STEP / 2}
                    cy={ratioY(point.downloaded / point.generated)}
                    r={2.5}
                    fill="#f59e0b"
                  />
                ) : null,
              )}
            </svg>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-200" /> 生成图片数
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" /> 已下载图片数
            </span>
            <span className="flex items-center gap-1.5 text-amber-600">
              <span className="inline-block h-0.5 w-4 rounded bg-amber-500" /> 下载率折线（0-100% 对应柱区高度）
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="space-y-1">
      <span className="block text-xs font-medium text-gray-500">{label}</span>
      {children}
    </label>
  )
}

function SummaryStrip({
  summary,
  loading,
}: {
  summary?: {
    task_count: number
    output_image_count: number
    completed_task_count: number
    failed_task_count: number
    credits_consumed: number
    downloaded_image_count: number
  }
  loading: boolean
}) {
  const metrics = [
    { label: '任务', value: summary?.task_count ?? 0 },
    { label: '图片输出', value: summary?.output_image_count ?? 0 },
    { label: '成功任务', value: summary?.completed_task_count ?? 0 },
    { label: '失败任务', value: summary?.failed_task_count ?? 0 },
    { label: 'Credits', value: summary?.credits_consumed ?? 0 },
    { label: '已下载', value: summary?.downloaded_image_count ?? 0 },
  ]
  return (
    <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
      {metrics.map((metric) => (
        <div key={metric.label} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <BarChart3 className="h-3.5 w-3.5" />
            {metric.label}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">
            {loading ? <span className="inline-block h-7 w-14 animate-pulse rounded bg-gray-200" /> : metric.value}
          </div>
        </div>
      ))}
    </div>
  )
}

type AssetTaskGroup = { taskId: string; items: MaterialAnalyticsChainItem[] }
type AssetInputGroup = { sig: string; tasks: AssetTaskGroup[] }

function AssetThumb({
  thumbnailUrl,
  url,
  className,
}: {
  thumbnailUrl: string | null
  url: string | null
  className: string
}) {
  const src = thumbnailUrl || url
  return (
    <div className={cn('overflow-hidden rounded-md bg-gray-100', className)}>
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <ImageIcon className="m-auto h-6 w-6 text-gray-400" />
      )}
    </div>
  )
}

function AssetTable({
  items,
  loading,
  selectedAssetId,
  onSelect,
}: {
  items: MaterialAnalyticsChainItem[]
  loading: boolean
  selectedAssetId: string | null
  onSelect: (assetId: string) => void
}) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set())

  // 翻页/筛选拿到新数据时清空展开状态，避免陈旧的 sig/taskId 残留
  useEffect(() => {
    setExpandedGroups(new Set())
    setExpandedTasks(new Set())
  }, [items])

  // 三级结构：输入图组（input_signature 相同的 task）→ task → 输出图片
  const inputGroups = useMemo(() => {
    const groups: AssetInputGroup[] = []
    const bySig = new Map<string, AssetInputGroup>()
    const byTask = new Map<string, AssetTaskGroup>()
    for (const item of items) {
      const sig = item.input_signature || item.task_id
      let group = bySig.get(sig)
      if (!group) {
        group = { sig, tasks: [] }
        bySig.set(sig, group)
        groups.push(group)
      }
      let task = byTask.get(item.task_id)
      if (!task) {
        task = { taskId: item.task_id, items: [] }
        byTask.set(item.task_id, task)
        group.tasks.push(task)
      }
      task.items.push(item)
    }
    return groups
  }, [items])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (!items.length) {
    return <div className="py-16 text-center text-sm text-muted-foreground">没有符合条件的图片素材。</div>
  }

  const toggleGroup = (group: AssetInputGroup) => {
    const next = new Set(expandedGroups)
    if (next.has(group.sig)) {
      next.delete(group.sig)
    } else {
      next.add(group.sig)
      // 展开时联动右侧详情：默认选中组内第一个任务的第一张图
      const hasSelected = group.tasks.some((task) =>
        task.items.some((item) => item.output.id === selectedAssetId),
      )
      if (!hasSelected) {
        onSelect(group.tasks[0].items[0].output.id)
      }
    }
    setExpandedGroups(next)
  }

  const toggleTask = (task: AssetTaskGroup) => {
    const next = new Set(expandedTasks)
    if (next.has(task.taskId)) {
      next.delete(task.taskId)
    } else {
      next.add(task.taskId)
      // 展开时联动右侧详情：默认选中该任务的第一张图
      if (!task.items.some((item) => item.output.id === selectedAssetId)) {
        onSelect(task.items[0].output.id)
      }
    }
    setExpandedTasks(next)
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left">输出</th>
            <th className="px-4 py-2 text-left">作者</th>
            <th className="px-4 py-2 text-left">下载</th>
            <th className="px-4 py-2 text-left">任务</th>
            <th className="px-4 py-2 text-left">产品</th>
            <th className="px-4 py-2 text-left">Prompt</th>
            <th className="px-4 py-2 text-left">时间</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {inputGroups.map((group) => {
            // 单任务组不增加层级，直接平铺任务行
            if (group.tasks.length === 1) {
              return (
                <TaskRows
                  key={group.tasks[0].taskId}
                  task={group.tasks[0]}
                  nested={false}
                  expanded={expandedTasks.has(group.tasks[0].taskId)}
                  selectedAssetId={selectedAssetId}
                  onToggle={() => toggleTask(group.tasks[0])}
                  onSelect={onSelect}
                />
              )
            }
            const isExpanded = expandedGroups.has(group.sig)
            const firstTask = group.tasks[0]
            const first = firstTask.items[0]
            // 缩略图优先取「在分组签名里」的输入图（签名是 md5/id token 集合），
            // 避免显示产品截图等未参与分组的输入
            const sigTokens = new Set(group.sig.split(','))
            const inputRef =
              first.inputs.find(
                (ref) => (ref.md5_hash && sigTokens.has(ref.md5_hash)) || sigTokens.has(ref.id),
              ) ?? first.inputs[0]
            const taskTotal = first.group_task_total ?? group.tasks.length
            const totalImages = group.tasks.reduce((acc, task) => acc + task.items.length, 0)
            const groupHasSelected = group.tasks.some((task) =>
              task.items.some((item) => item.output.id === selectedAssetId),
            )
            const anyDownloaded = group.tasks.some((task) =>
              task.items.some((item) => item.download.is_downloaded),
            )
            const authors = [
              ...new Set(group.tasks.map((task) => task.items[0].author_email.split('@')[0])),
            ]
            const products = [
              ...new Set(
                group.tasks.map(
                  (task) =>
                    task.items[0].material_product_name || task.items[0].product_name || '未匹配',
                ),
              ),
            ]
            return (
              <Fragment key={group.sig}>
                <tr
                  className={cn(
                    'cursor-pointer',
                    groupHasSelected && !isExpanded
                      ? 'bg-blue-50/70'
                      : 'bg-indigo-50/40 hover:bg-indigo-50/70',
                  )}
                  onClick={() => toggleGroup(group)}
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 shrink-0 text-gray-500 transition-transform',
                          isExpanded && 'rotate-90',
                        )}
                      />
                      <div className="relative">
                        <AssetThumb
                          thumbnailUrl={inputRef?.thumbnail_url ?? null}
                          url={inputRef?.url ?? null}
                          className="h-14 w-14 ring-2 ring-indigo-200"
                        />
                        <span className="absolute bottom-0 right-0 rounded-tl bg-indigo-600/90 px-1 text-[10px] text-white">
                          输入图
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {authors.slice(0, 2).join('、')}
                    {authors.length > 2 ? ` 等${authors.length}人` : ''}
                  </td>
                  <td className="px-4 py-2">
                    {anyDownloaded ? <span className="text-emerald-600">已下载</span> : '未记录'}
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">同输入图 · {taskTotal} 个任务</div>
                    <div className="text-xs text-gray-500">
                      {taskTotal > group.tasks.length
                        ? `已加载最新 ${group.tasks.length} 个任务 / ${totalImages} 张输出`
                        : `共 ${totalImages} 张输出`}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {products.length > 1 ? `${products[0]} 等${products.length}个` : products[0]}
                  </td>
                  <td className="px-4 py-2 text-gray-400">—</td>
                  <td className="px-4 py-2 text-gray-500">
                    {formatDateTime(firstTask.items[0].task_created_at)}
                  </td>
                </tr>
                {isExpanded &&
                  group.tasks.map((task) => (
                    <TaskRows
                      key={task.taskId}
                      task={task}
                      nested
                      expanded={expandedTasks.has(task.taskId)}
                      selectedAssetId={selectedAssetId}
                      onToggle={() => toggleTask(task)}
                      onSelect={onSelect}
                    />
                  ))}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TaskRows({
  task,
  nested,
  expanded,
  selectedAssetId,
  onToggle,
  onSelect,
}: {
  task: AssetTaskGroup
  nested: boolean
  expanded: boolean
  selectedAssetId: string | null
  onToggle: () => void
  onSelect: (assetId: string) => void
}) {
  const first = task.items[0]
  const hasSelected = task.items.some((item) => item.output.id === selectedAssetId)
  const anyDownloaded = task.items.some((item) => item.download.is_downloaded)
  return (
    <Fragment>
      <tr
        className={cn(
          'cursor-pointer hover:bg-gray-50',
          hasSelected && !expanded && 'bg-blue-50/70',
        )}
        onClick={onToggle}
      >
        <td className="px-4 py-2">
          <div className={cn('flex items-center gap-1.5', nested && 'pl-6')}>
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 text-gray-400 transition-transform',
                expanded && 'rotate-90',
              )}
            />
            <div className="relative">
              <AssetThumb
                thumbnailUrl={first.output.thumbnail_url}
                url={first.output.url}
                className="h-14 w-14"
              />
              {task.items.length > 1 && (
                <span className="absolute bottom-0 right-0 rounded-tl bg-gray-900/70 px-1 text-[10px] text-white">
                  ×{task.items.length}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-2 text-gray-700">{first.author_email.split('@')[0]}</td>
        <td className="px-4 py-2">
          {anyDownloaded ? <span className="text-emerald-600">已下载</span> : '未记录'}
        </td>
        <td className="px-4 py-2">
          <div className="font-medium text-gray-900">{first.task_type}</div>
          <div className="text-xs text-gray-500">{first.tool_id} · {first.task_status}</div>
        </td>
        <td className="px-4 py-2 text-gray-700">
          {first.material_product_name || first.product_name || '未匹配'}
        </td>
        <td className="px-4 py-2">
          {task.items.some((item) => item.prompts.missing_fields.length) ? (
            <span className="rounded-full bg-amber-50 px-2 py-1 text-xs text-amber-700">部分缺失</span>
          ) : (
            <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs text-emerald-700">完整</span>
          )}
        </td>
        <td className="px-4 py-2 text-gray-500">{formatDateTime(first.task_created_at)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-gray-50/60 px-4 py-3">
            <div className={cn('flex flex-wrap gap-3', nested ? 'pl-11' : 'pl-5')}>
              {task.items.map((item) => (
                <button
                  key={item.output.id}
                  type="button"
                  onClick={() => onSelect(item.output.id)}
                  className={cn(
                    'flex w-20 flex-col items-center gap-1 rounded-md border p-1.5 text-left',
                    selectedAssetId === item.output.id
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-transparent hover:border-gray-200 hover:bg-white',
                  )}
                >
                  <AssetThumb
                    thumbnailUrl={item.output.thumbnail_url}
                    url={item.output.url}
                    className="h-16 w-16 rounded"
                  />
                  <div className="text-[11px] text-gray-500">
                    #{item.sequence_num ?? '-'}
                    {item.download.is_downloaded && (
                      <span className="ml-1 text-emerald-600">已下载</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

function ChainDetail({
  chain,
  products,
  loading,
  onBind,
  binding,
}: {
  chain: MaterialAnalyticsChainItem | null
  products: MaterialAnalyticsProduct[]
  loading: boolean
  onBind: (assetId: string, productId: string | null) => void
  binding: boolean
}) {
  const [bindValue, setBindValue] = useState('')

  useEffect(() => {
    setBindValue(chain?.material_product_id || '')
  }, [chain?.material_product_id, chain?.output.id])

  if (!chain) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-muted-foreground">
        选择一张图片查看完整链路。
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <div className="font-semibold text-gray-900">链路详情</div>
          <div className="text-xs text-muted-foreground">{chain.output.id}</div>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <div className="space-y-4 p-4">
        <Section title="输入图片">
          {chain.inputs.length ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 2xl:grid-cols-5">
              {chain.inputs.map((input) => (
                <div key={`${input.role}-${input.id}`} className="space-y-1">
                  <div className="aspect-square overflow-hidden rounded bg-gray-100">
                    {input.thumbnail_url || input.url ? (
                      <img src={input.thumbnail_url || input.url || ''} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon className="m-6 h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div className="truncate text-[11px] text-gray-500">{input.role}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">现有数据未记录可还原的输入图片。</div>
          )}
        </Section>

        <Section title="Prompt">
          <PromptBlock title="System" value={chain.prompts.prompt_system} />
          <PromptBlock title="Visual" value={chain.prompts.prompt_visual} />
          <PromptBlock title="User" value={chain.prompts.prompt_user} />
          <PromptBlock title="Final" value={chain.prompts.prompt_final || chain.prompts.asset_prompt} />
          {chain.prompts.missing_fields.length > 0 && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              缺失字段：{chain.prompts.missing_fields.join(', ')}
            </div>
          )}
        </Section>

        <Section title="输出图片">
          {chain.output.thumbnail_url || chain.output.url ? (
            // 体验优先：默认加载缩略图，仅在无缩略图的旧数据上回退原图
            <img
              src={chain.output.thumbnail_url || chain.output.url || ''}
              alt=""
              className="max-h-[420px] w-full rounded-md object-contain bg-gray-50"
            />
          ) : (
            <div className="rounded-md bg-gray-50 px-3 py-8 text-center text-sm text-muted-foreground">
              现有数据未记录可预览的输出图片。
            </div>
          )}
        </Section>

        <div className="space-y-4 border-t border-gray-100 pt-4">
          <Section title="任务信息">
            <div className="grid grid-cols-2 gap-2 text-sm 2xl:grid-cols-4">
              <Info label="作者" value={chain.author_email} />
              <Info label="任务" value={`${chain.task_type} / ${chain.tool_id}`} />
              <Info label="状态" value={chain.task_status} />
              <Info label="序号" value={chain.sequence_num ? String(chain.sequence_num) : '-'} />
              <Info label="原产品" value={chain.product_name || '-'} />
              <Info label="分析产品" value={chain.material_product_name || '-'} />
              <Info label="MD5" value={chain.output.md5_hash || '-'} mono />
              <Info label="pHash" value={chain.output.phash || '-'} mono />
            </div>
          </Section>

          <Section title="绑定分析产品">
            <div className="flex gap-2">
              <select
                value={bindValue}
                onChange={(event) => setBindValue(event.target.value)}
                className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">不绑定</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={binding}
                onClick={() => onBind(chain.output.id, bindValue || null)}
              >
                保存
              </Button>
            </div>
          </Section>

        <Section title="下载情况">
          <div className="text-sm text-gray-700">
            {chain.download.is_downloaded ? '已下载' : '未下载或未记录'}
            {chain.download.download_count !== null && ` · ${chain.download.download_count} 次`}
          </div>
          {chain.download.missing_reason && (
            <div className="mt-1 text-xs text-muted-foreground">{chain.download.missing_reason}</div>
          )}
        </Section>

        <Section title="Generation Params">
          <pre className="max-h-64 overflow-auto rounded-md bg-gray-950 p-3 text-xs text-gray-100">
            {JSON.stringify(chain.generation_params, null, 2)}
          </pre>
        </Section>
        </div>
      </div>
    </div>
  )
}

function Info({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded-md bg-gray-50 px-3 py-2">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className={cn('truncate text-sm text-gray-900', mono && 'font-mono text-xs')}>{value}</div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</div>
      {children}
    </div>
  )
}

function PromptBlock({ title, value }: { title: string; value: string | null }) {
  if (!value) return null
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="mb-1 text-xs font-medium text-gray-500">{title}</div>
      <div className="max-h-52 overflow-auto whitespace-pre-wrap text-xs leading-5 text-gray-800">{value}</div>
    </div>
  )
}

function SearchResults({
  results,
  onSelect,
}: {
  results: MaterialAnalyticsImageSearchResult[]
  onSelect: (assetId: string) => void
}) {
  if (!results.length) {
    return <div className="text-sm text-muted-foreground">上传图片后会按文件分组显示命中链路。</div>
  }
  return (
    <div className="space-y-4">
      {results.map((result) => (
        <div key={`${result.file_name}-${result.md5_hash}`} className="rounded-md border border-gray-200">
          <div className="border-b border-gray-100 px-4 py-2">
            <div className="font-medium text-gray-900">{result.file_name}</div>
            <div className="text-xs text-gray-500">MD5 {result.md5_hash || '-'} · pHash {result.phash || '-'}</div>
          </div>
          {result.error ? (
            <div className="px-4 py-3 text-sm text-red-600">{result.error}</div>
          ) : !result.matches.length ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">没有命中。</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {result.matches.map((match, index) => (
                <button
                  key={`${match.asset_id}-${index}`}
                  className="flex w-full items-center justify-between px-4 py-2 text-left hover:bg-gray-50"
                  onClick={() => match.chain && onSelect(match.chain.output.id)}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{match.match_type}</div>
                    <div className="text-xs text-gray-500">
                      {match.chain?.author_email || '无链路'} · {match.chain?.task_type || match.asset_id}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {match.distance !== null ? `distance ${match.distance}` : 'exact'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ProductList({ products, loading }: { products: MaterialAnalyticsProduct[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex justify-center rounded-lg border border-gray-200 bg-white py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3 font-medium">分析产品</div>
      <div className="divide-y divide-gray-100">
        {products.length ? (
          products.map((product) => (
            <div key={product.id} className="px-4 py-3">
              <div className="font-medium text-gray-900">{product.product_name}</div>
              <div className="mt-1 text-sm text-gray-600">{product.description || '未填写描述'}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                {product.target_market && <span>市场：{product.target_market}</span>}
                {product.target_audience && <span>受众：{product.target_audience}</span>}
                <span>owner：{product.owner_email}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">还没有分析产品。</div>
        )}
      </div>
    </div>
  )
}
